/**
 * LookupSourceEditor — sub-form rendered by MergeFieldMapper when a
 * field mapping has sourceType='lookup'. Lets the marketer pick a
 * lookup table, the value column, the contact attribute that keys
 * the lookup, optional filter rules on rule columns, and an optional
 * fallback string.
 *
 * Validation surface (rendered inline, doesn't block — PreSend modal
 * is the gate that hard-blocks send on red flags):
 *
 *   - lookupId not set                    → amber "Pick a lookup"
 *   - lookupId points to deleted lookup   → red "Lookup deleted"
 *   - lookupValueColumn not set           → amber "Pick a value column"
 *   - lookupValueColumn not in schema     → red "Column missing"
 *   - lookupKeyContactField not set       → amber "Pick a contact field"
 *   - filter rule's column not in schema  → red "Column missing"
 */

import { useQuery } from "@tanstack/react-query";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  getLookups,
  getLookup,
  getLookupRows,
} from "../../services/lookups.service";
import {
  CONTACT_FIELDS,
  type FieldMapping,
  type LookupFilterOperator,
  type LookupFilterRule,
} from "../../services/templates.service";

interface LookupSourceEditorProps {
  mapping: FieldMapping;
  onChange: (updates: Partial<FieldMapping>) => void;
}

const OPERATORS: { value: LookupFilterOperator; label: string }[] = [
  { value: "eq", label: "equals" },
  { value: "ne", label: "not equals" },
  { value: "in", label: "is one of" },
  { value: "not_in", label: "is not one of" },
  { value: "is_truthy", label: "is yes/true" },
  { value: "is_falsy", label: "is no/false" },
];

// Operators that don't need a value field rendered (the value is implicit)
const VALUELESS_OPERATORS: ReadonlySet<LookupFilterOperator> = new Set([
  "is_truthy",
  "is_falsy",
]);

// Operators that take a comma-separated array of values (rendered as a
// single text input with a small hint).
const ARRAY_OPERATORS: ReadonlySet<LookupFilterOperator> = new Set([
  "in",
  "not_in",
]);

/**
 * Coerce a filter rule's existing value to the shape the new operator
 * expects. Without this, switching `eq → in` while the value is "TX"
 * leaves the saved value as the scalar "TX", and the resolver's
 * `Array.isArray(rule.value) ? rule.value : []` check yields `[]` and
 * fails the filter closed. Round-3-followup audit blocker.
 *
 * Rules:
 *   - target is_truthy / is_falsy → undefined (value ignored)
 *   - target in / not_in:
 *       - already array → keep
 *       - scalar string → split by comma, trim, filter blanks
 *       - undefined → []
 *   - target eq / ne:
 *       - array → join with ", "
 *       - scalar string → keep
 *       - undefined → ""
 *
 * Exported for unit testing.
 */
export function coerceValueForOperator(
  current: string | string[] | undefined,
  next: LookupFilterOperator,
): string | string[] | undefined {
  if (VALUELESS_OPERATORS.has(next)) {
    return undefined;
  }
  if (ARRAY_OPERATORS.has(next)) {
    if (Array.isArray(current)) {
      return current;
    }
    if (typeof current === "string" && current.length > 0) {
      return current
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return [];
  }
  // eq / ne — scalar
  if (Array.isArray(current)) {
    return current.join(", ");
  }
  return current ?? "";
}

export default function LookupSourceEditor({
  mapping,
  onChange,
}: LookupSourceEditorProps) {
  // Load the list of all lookups for the picker.
  const lookupsQ = useQuery({
    queryKey: ["campaign-lookups"],
    queryFn: getLookups,
  });

  // Load the selected lookup's column_schema so we can render the
  // value/filter column dropdowns and validate references.
  const selectedQ = useQuery({
    queryKey: ["campaign-lookup", mapping.lookupId],
    queryFn: () => (mapping.lookupId ? getLookup(mapping.lookupId) : null),
    enabled: !!mapping.lookupId,
  });

  const selected = selectedQ.data ?? null;
  const schemaColumns = selected?.column_schema ?? [];
  const schemaColumnNames = schemaColumns.map((c) => c.name);
  const resolutionMode = mapping.lookupResolution ?? "keyed";
  const defaultRowsQ = useQuery({
    queryKey: ["campaign-lookup-rows", mapping.lookupId],
    queryFn: () => (mapping.lookupId ? getLookupRows(mapping.lookupId) : []),
    enabled: !!mapping.lookupId && resolutionMode === "default_row",
  });
  const hasDefaultRow = (defaultRowsQ.data ?? []).some((r) => r.is_default);

  // When a lookup is picked, we want to default lookupKeyContactField to
  // a contact field whose value matches the lookup's key_column (case-
  // insensitive). The marketer can override the choice.
  const handleLookupChange = (lookupId: string) => {
    const updates: Partial<FieldMapping> = {
      lookupId,
      // Clear schema-dependent fields when switching lookups
      lookupValueColumn: undefined,
      lookupFilter: undefined,
    };
    if (lookupId) {
      const lookup = lookupsQ.data?.find((l) => l.id === lookupId);
      if (lookup && !mapping.lookupKeyContactField) {
        const keyLower = lookup.key_column.toLowerCase();
        const matchedField = CONTACT_FIELDS.find(
          (f) => f.value.toLowerCase() === keyLower,
        );
        if (matchedField) {
          updates.lookupKeyContactField = matchedField.value;
        }
      }
    }
    onChange(updates);
  };

  const handleFilterChange = (newFilter: LookupFilterRule[] | undefined) => {
    onChange({ lookupFilter: newFilter });
  };

  const filter = mapping.lookupFilter ?? [];

  // Validation flags
  const lookupExists = !!selected;
  const valueColumnExists =
    !!mapping.lookupValueColumn &&
    schemaColumnNames.includes(mapping.lookupValueColumn);
  const filterColumnFlags = filter.map((rule) =>
    schemaColumnNames.includes(rule.column),
  );

  return (
    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3 space-y-3">
      <div className="text-xs font-medium text-blue-900 uppercase tracking-wide">
        Lookup configuration
      </div>

      {/* 1. Lookup picker */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Lookup table
        </label>
        {lookupsQ.isLoading ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : (
          <select
            value={mapping.lookupId ?? ""}
            onChange={(e) => handleLookupChange(e.target.value)}
            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Pick a lookup…</option>
            {(lookupsQ.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.key_column})
              </option>
            ))}
          </select>
        )}
        {mapping.lookupId && !lookupExists && !selectedQ.isLoading && (
          <ValidationBadge
            level="error"
            text="Lookup has been deleted. Pick another."
          />
        )}
        {!mapping.lookupId && (
          <ValidationBadge level="warn" text="Pick a lookup" />
        )}
      </div>

      {/* 2. Value column picker */}
      {mapping.lookupId && lookupExists && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Value column
          </label>
          <select
            value={mapping.lookupValueColumn ?? ""}
            onChange={(e) =>
              onChange({ lookupValueColumn: e.target.value || undefined })
            }
            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Pick a value column…</option>
            {schemaColumns
              .filter((c) => c.name !== selected.key_column)
              .map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                  {c.role ? ` (${c.role})` : ""}
                </option>
              ))}
          </select>
          {!mapping.lookupValueColumn && (
            <ValidationBadge
              level="warn"
              text="Pick the column whose value should substitute"
            />
          )}
          {mapping.lookupValueColumn && !valueColumnExists && (
            <ValidationBadge
              level="error"
              text={`Column "${mapping.lookupValueColumn}" no longer exists in this lookup. Pick another.`}
            />
          )}
        </div>
      )}

      {/* 3. Contact key field picker */}
      {mapping.lookupId && lookupExists && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            How should this lookup resolve?
          </label>
          <div className="space-y-1 rounded border border-blue-100 bg-white p-2">
            <label className="flex items-start gap-2 text-xs text-gray-700">
              <input
                type="radio"
                checked={resolutionMode === "default_row"}
                onChange={() => onChange({ lookupResolution: "default_row" })}
                className="mt-0.5"
              />
              <span>
                <strong>Static content bundle</strong> — use the lookup's marked
                default row for every recipient.
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs text-gray-700">
              <input
                type="radio"
                checked={resolutionMode === "keyed"}
                onChange={() => onChange({ lookupResolution: "keyed" })}
                className="mt-0.5"
              />
              <span>
                <strong>Match each recipient</strong> — match a contact field to
                the lookup key (existing behavior).
              </span>
            </label>
          </div>
          {resolutionMode === "default_row" &&
            !defaultRowsQ.isLoading &&
            !hasDefaultRow && (
              <ValidationBadge
                level="error"
                text="This lookup has no default row. Open it and mark one row with the star."
              />
            )}
        </div>
      )}

      {/* 3. Contact key field picker (keyed mode only) */}
      {mapping.lookupId && lookupExists && resolutionMode === "keyed" && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Match against contact field
          </label>
          <select
            value={mapping.lookupKeyContactField ?? ""}
            onChange={(e) =>
              onChange({ lookupKeyContactField: e.target.value || undefined })
            }
            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Pick a contact field…</option>
            {CONTACT_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          {!mapping.lookupKeyContactField && (
            <ValidationBadge
              level="warn"
              text="Pick the contact attribute that matches the lookup key"
            />
          )}
          <p className="text-xs text-gray-500 mt-1">
            Lookup matches case-insensitively against this contact value (key
            column: <code className="font-mono">{selected.key_column}</code>).
          </p>
        </div>
      )}

      {/* 4. Optional filter builder (recipient-keyed mode only) */}
      {mapping.lookupId && lookupExists && resolutionMode === "keyed" && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Eligibility filter (optional)
          </label>
          {filter.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              No filter — every matched row is eligible.
            </p>
          ) : (
            <div className="space-y-1">
              {filter.map((rule, idx) => (
                <FilterRuleRow
                  key={idx}
                  rule={rule}
                  schemaColumns={schemaColumnNames}
                  columnExists={filterColumnFlags[idx]}
                  onChange={(updated) => {
                    const next = [...filter];
                    next[idx] = updated;
                    handleFilterChange(next);
                  }}
                  onRemove={() => {
                    const next = filter.filter((_, i) => i !== idx);
                    handleFilterChange(next.length === 0 ? undefined : next);
                  }}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() =>
              handleFilterChange([
                ...filter,
                {
                  column: schemaColumnNames[0] ?? "",
                  operator: "eq",
                  value: "",
                },
              ])
            }
            className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            + Add filter rule
          </button>
          {filter.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Rules AND together. If a matched row fails the filter, the default
              row is tried; if the default row also fails, the fallback below is
              used.
            </p>
          )}
        </div>
      )}

      {/* 5. Fallback value */}
      {mapping.lookupId && lookupExists && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Fallback value (optional)
          </label>
          <input
            type="text"
            value={mapping.lookupFallback ?? ""}
            onChange={(e) =>
              onChange({ lookupFallback: e.target.value || undefined })
            }
            placeholder="(empty — variable renders blank if no row matches)"
            className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Used when the contact's key has no match AND no default row passes
            the filter.
          </p>
        </div>
      )}
    </div>
  );
}

function FilterRuleRow({
  rule,
  schemaColumns,
  columnExists,
  onChange,
  onRemove,
}: {
  rule: LookupFilterRule;
  schemaColumns: string[];
  columnExists: boolean;
  onChange: (updated: LookupFilterRule) => void;
  onRemove: () => void;
}) {
  const valueless = VALUELESS_OPERATORS.has(rule.operator);
  const arrayValue = ARRAY_OPERATORS.has(rule.operator);
  const valueAsString = (() => {
    if (rule.value === undefined) {
      return "";
    }
    if (Array.isArray(rule.value)) {
      return rule.value.join(", ");
    }
    return rule.value;
  })();

  const handleValueChange = (raw: string) => {
    if (arrayValue) {
      const parsed = raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      onChange({ ...rule, value: parsed });
    } else {
      onChange({ ...rule, value: raw });
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <select
        value={rule.column}
        onChange={(e) => onChange({ ...rule, column: e.target.value })}
        className={`text-xs px-2 py-1 border rounded focus:ring-1 focus:ring-primary-500 ${
          columnExists ? "border-gray-300" : "border-red-400 bg-red-50"
        }`}
      >
        {!schemaColumns.includes(rule.column) && (
          <option value={rule.column}>{rule.column}</option>
        )}
        {schemaColumns.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <select
        value={rule.operator}
        onChange={(e) => {
          const nextOp = e.target.value as LookupFilterOperator;
          onChange({
            ...rule,
            operator: nextOp,
            value: coerceValueForOperator(rule.value, nextOp),
          });
        }}
        className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
      >
        {OPERATORS.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>
      {!valueless && (
        <input
          type="text"
          value={valueAsString}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={arrayValue ? "value1, value2" : "value"}
          className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 flex-1 min-w-[100px]"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        title="Remove rule"
        className="p-1 text-gray-400 hover:text-red-600"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
      {!columnExists && (
        <ValidationBadge
          level="error"
          text={`Column "${rule.column}" not in lookup`}
          inline
        />
      )}
    </div>
  );
}

function ValidationBadge({
  level,
  text,
  inline = false,
}: {
  level: "warn" | "error";
  text: string;
  inline?: boolean;
}) {
  const colors =
    level === "error"
      ? "text-red-700 bg-red-50 border-red-200"
      : "text-amber-700 bg-amber-50 border-amber-200";
  return (
    <span
      className={`${inline ? "inline-flex" : "mt-1 inline-flex"} items-center gap-1 px-2 py-0.5 text-xs border rounded ${colors}`}
    >
      <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" />
      {text}
    </span>
  );
}
